import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { TrendingDown, FileText, Users, Award, BarChart3, PiggyBank, Percent, Info, Calculator, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const SavingsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalculation, setShowCalculation] = useState(false);

  useEffect(() => {
    fetchSavingsData();
  }, []);

  const fetchSavingsData = async () => {
    try {
      const response = await apiClient.get('/dashboard/savings');
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching savings data:', error);
      toast.error('Failed to load savings data');
      setLoading(false);
    }
  };

  // Generic number formatting without currency symbols
  const formatNumber = (value) => {
    if (!value) return '0.00';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="savings-loading">
        Loading savings data...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="savings-error">
        Failed to load savings data
      </div>
    );
  }

  return (
    <div data-testid="savings-dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="savings-title">
          Savings Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Track your procurement savings and efficiency metrics
        </p>
      </div>

      {/* Main KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Savings */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg" data-testid="total-savings-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <PiggyBank size={24} />
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">Total</span>
          </div>
          <div className="text-3xl font-bold mb-1">{formatNumber(data.total_savings)}</div>
          <div className="text-green-100 text-sm">Total Savings Achieved</div>
        </div>

        {/* Savings Percentage */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg" data-testid="savings-percentage-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Percent size={24} />
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">Rate</span>
          </div>
          <div className="text-3xl font-bold mb-1">{data.savings_percentage.toFixed(1)}%</div>
          <div className="text-blue-100 text-sm">Average Savings Rate</div>
        </div>

        {/* Potential Spend */}
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg" data-testid="potential-spend-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <TrendingDown size={24} />
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">Avoided</span>
          </div>
          <div className="text-3xl font-bold mb-1">{formatNumber(data.potential_spend)}</div>
          <div className="text-amber-100 text-sm">Potential Spend (Highest Quotes)</div>
        </div>

        {/* Awarded Value */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg" data-testid="awarded-value-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Award size={24} />
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">Actual</span>
          </div>
          <div className="text-3xl font-bold mb-1">{formatNumber(data.total_awarded_value)}</div>
          <div className="text-purple-100 text-sm">Total Awarded Value</div>
        </div>
      </div>

      {/* How Savings are Calculated - Expandable Section */}
      <div className="bg-white rounded-lg border border-slate-200 mb-8 overflow-hidden" data-testid="savings-calculation-section">
        <button
          onClick={() => setShowCalculation(!showCalculation)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calculator size={20} className="text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="text-base font-semibold text-slate-900">How are savings calculated?</h3>
              <p className="text-xs text-slate-500">Click to see the detailed calculation methodology</p>
            </div>
          </div>
          {showCalculation ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
        </button>
        
        {showCalculation && (
          <div className="px-6 pb-6 border-t border-slate-100">
            <div className="mt-4 space-y-4">
              {/* Formula Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Total Savings Formula */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <PiggyBank size={16} className="text-green-600" />
                    <span className="font-semibold text-green-800">Total Savings</span>
                  </div>
                  <div className="bg-white rounded p-3 font-mono text-sm text-slate-700 mb-2">
                    = Potential Spend − Awarded Value
                  </div>
                  <p className="text-xs text-green-700">
                    The difference between what you could have spent (highest quotes) and what you actually spent (awarded quotes).
                  </p>
                </div>

                {/* Savings Rate Formula */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent size={16} className="text-blue-600" />
                    <span className="font-semibold text-blue-800">Savings Rate (%)</span>
                  </div>
                  <div className="bg-white rounded p-3 font-mono text-sm text-slate-700 mb-2">
                    = (Total Savings ÷ Potential Spend) × 100
                  </div>
                  <p className="text-xs text-blue-700">
                    Percentage of money saved compared to the maximum you could have spent.
                  </p>
                </div>

                {/* Potential Spend Formula */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown size={16} className="text-amber-600" />
                    <span className="font-semibold text-amber-800">Potential Spend (Avoided Cost)</span>
                  </div>
                  <div className="bg-white rounded p-3 font-mono text-sm text-slate-700 mb-2">
                    = Sum of (Highest Quote × Qty) per item
                  </div>
                  <p className="text-xs text-amber-700">
                    The maximum amount you would have spent if you chose the highest-priced supplier for each item.
                  </p>
                </div>

                {/* Awarded Value Formula */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Award size={16} className="text-purple-600" />
                    <span className="font-semibold text-purple-800">Awarded Value (Actual Cost)</span>
                  </div>
                  <div className="bg-white rounded p-3 font-mono text-sm text-slate-700 mb-2">
                    = Sum of (Awarded Quote × Qty) per item
                  </div>
                  <p className="text-xs text-purple-700">
                    The actual amount spent based on the quotes you awarded to suppliers.
                  </p>
                </div>
              </div>

              {/* Example Calculation */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info size={16} className="text-slate-600" />
                  <span className="font-semibold text-slate-800">Example Calculation</span>
                </div>
                <div className="text-sm text-slate-600 space-y-2">
                  <p>For an item with Qty = 100:</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Highest Quote: 150/unit → Potential Spend = 150 × 100 = <span className="font-semibold">15,000</span></li>
                    <li>Awarded Quote: 120/unit → Awarded Value = 120 × 100 = <span className="font-semibold">12,000</span></li>
                    <li>Savings = 15,000 − 12,000 = <span className="font-semibold text-green-600">3,000</span></li>
                    <li>Savings Rate = (3,000 ÷ 15,000) × 100 = <span className="font-semibold text-blue-600">20%</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid="total-rfqs-stat">
          <div className="p-3 bg-slate-100 rounded-lg">
            <FileText size={20} className="text-slate-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{data.total_rfqs}</div>
            <div className="text-xs text-slate-500">Total RFQs Created</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid="awarded-rfqs-stat">
          <div className="p-3 bg-green-100 rounded-lg">
            <Award size={20} className="text-green-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{data.awarded_rfqs}</div>
            <div className="text-xs text-slate-500">RFQs with Awards</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid="total-suppliers-stat">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Users size={20} className="text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{data.total_suppliers}</div>
            <div className="text-xs text-slate-500">Total Suppliers</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid="avg-quotes-stat">
          <div className="p-3 bg-amber-100 rounded-lg">
            <BarChart3 size={20} className="text-amber-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{data.avg_quotes_per_rfq.toFixed(1)}</div>
            <div className="text-xs text-slate-500">Avg Quotes per RFQ</div>
          </div>
        </div>
      </div>

      {/* Savings by Month Chart */}
      {Object.keys(data.savings_by_month).length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-8" data-testid="savings-by-month">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Savings by Month</h2>
          <div className="space-y-3">
            {Object.entries(data.savings_by_month).slice(-6).map(([month, savings]) => {
              const maxSavings = Math.max(...Object.values(data.savings_by_month));
              const percentage = maxSavings > 0 ? (savings / maxSavings) * 100 : 0;
              const monthName = new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              
              return (
                <div key={month} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-slate-600">{monthName}</div>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-green-400 to-green-600 h-full rounded-full flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      <span className="text-xs font-medium text-white">
                        {formatNumber(savings)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Savings by Category */}
      {Object.keys(data.savings_by_category).length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="savings-by-category">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Savings by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(data.savings_by_category)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 6)
              .map(([category, savings]) => (
                <div key={category} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-700">{category || 'Uncategorized'}</span>
                  <span className="text-sm font-bold text-green-600">{formatNumber(savings)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {data.total_savings === 0 && (
        <div className="bg-slate-50 rounded-lg border-2 border-dashed border-slate-200 p-12 text-center" data-testid="no-savings-message">
          <PiggyBank size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Savings Data Yet</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Start awarding RFQs to track your procurement savings. The dashboard will show how much you've saved by choosing competitive quotes.
          </p>
        </div>
      )}
    </div>
  );
};

export default SavingsDashboard;
